import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import ZStudioNav from '@/pages/dashboard/apps/zstudio/components/ZStudioNav';
import ZRecentGenerations from '@/pages/dashboard/apps/zstudio/components/ZRecentGenerations';
import {
  createZProject,
  createZCreation,
  logZGeneration,
  generateZImage,
  zLookbookCopy,
  listZProducts,
  firstProductImage,
  Z_QUALITIES,
  zQualityToApi,
  type ZProductItem,
} from '@/lib/zstudio';

export default function ZStudioLookbookPage() {
  const { user } = useAuth();
  const [title, setTitle] = useState('Nouvelle collection');
  const [selected, setSelected] = useState<number[]>([]);
  const [products, setProducts] = useState<ZProductItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [quality, setQuality] = useState('standard');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const loadRef = async () => {
    if (loaded) return;
    setLoaded(true);
    try { setProducts(await listZProducts()); } catch { /* ignore */ }
  };
  if (!loaded) loadRef();

  const toggle = (id: number) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const run = async () => {
    if (!user?.id || selected.length === 0) { setError('Sélectionnez au moins une pièce.'); return; }
    setRunning(true);
    setError(null);
    setDone(false);
    try {
      const items = products.filter((p) => selected.includes(p.id));
      const project = await createZProject({ ownerId: user.id, name: title.slice(0, 120), kind: 'fashion', sourceType: 'collection', meta: { count: items.length } });

      setProgress('Rédaction du lookbook…');
      const copy = await zLookbookCopy({
        collection: title,
        items: items.map((p, i) => ({ ref: `REF${String(i + 1).padStart(3, '0')}`, name: p.name, price: p.price != null ? String(p.price) : undefined })),
        language: 'fr',
      });

      setProgress('Génération de la couverture…');
      const q = zQualityToApi(quality);
      const cover = await generateZImage({
        prompt: `Elegant fashion lookbook cover for a collection titled "${title}". Editorial styling, refined minimal composition, soft cinematic lighting, premium fashion photography, no text, no watermark.`,
        aspectRatio: '3:4',
        quality: q.quality,
        resolution: q.resolution,
        persist: true,
        folder: 'zstudio/lookbook',
      });

      const content = {
        title: copy.title || title,
        intro: copy.intro,
        pages: copy.pages,
        items: items.map((p) => ({ id: p.id, name: p.name, price: p.price, image: firstProductImage(p.media) })),
      };
      await createZCreation({
        ownerId: user.id,
        projectId: project.id,
        kind: 'lookbook',
        mediaKind: 'text',
        url: [copy.title || title, copy.intro, ...(copy.pages || []).map((pg) => `${pg.title} — ${pg.description}`)].join('\n\n'),
        thumbnail_url: cover[0] || null,
        title: `Lookbook — ${title}`,
        content,
        provider: 'xAI',
      });
      if (cover[0]) {
        await createZCreation({ ownerId: user.id, projectId: project.id, kind: 'lookbook', mediaKind: 'image', url: cover[0], thumbnail_url: cover[0], title: `Couverture — ${title}`, content: { role: 'cover' }, provider: 'xAI' });
      }
      await logZGeneration({ ownerId: user.id, projectId: project.id, type: 'lookbook', prompt: title, provider: 'xAI', output: { pages: copy.pages?.length || 0 }, status: 'done' });
      setProgress('');
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La génération a échoué.');
      setProgress('');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="mb-4">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-book-open-line text-accent-500"></i>Lookbook IA
        </h2>
        <p className="text-sm text-foreground-500 mt-1">Sélectionnez plusieurs pièces d’une collection : ZStudio rédige le catalogue et génère une couverture (spec 40 & 39).</p>
      </div>
      <ZStudioNav />
      <ZRecentGenerations />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-background-50 border border-background-200/70 rounded-xl p-4">
          <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nom de la collection</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-300 mb-3" />
          <p className="text-xs font-medium text-foreground-600 mb-2">Pièces ({selected.length} sélectionnée{selected.length > 1 ? 's' : ''})</p>
          {products.length === 0 ? (
            <p className="text-xs text-foreground-500 py-8 text-center">Aucun produit Zifek disponible.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[420px] overflow-y-auto">
              {products.map((p) => {
                const img = firstProductImage(p.media);
                const active = selected.includes(p.id);
                return (
                  <button key={p.id} type="button" onClick={() => toggle(p.id)} className={`text-left rounded-lg border overflow-hidden transition-colors cursor-pointer ${active ? 'border-primary-400 ring-1 ring-primary-300' : 'border-background-200/70 hover:border-primary-300'}`}>
                    <div className="aspect-square bg-background-100 relative">
                      {img ? <img src={img} alt={p.name} className="w-full h-full object-cover object-top" /> : <div className="w-full h-full flex items-center justify-center"><i className="ri-shopping-bag-3-line text-foreground-400"></i></div>}
                      {active && <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary-500 text-background-50 flex items-center justify-center"><i className="ri-check-line text-xs"></i></span>}
                    </div>
                    <div className="p-2">
                      <p className="text-[11px] font-medium text-foreground-800 truncate">{p.name}</p>
                      <p className="text-[10px] text-foreground-500 truncate">{p.price != null ? `${p.price} ${p.currency || ''}` : '—'}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <p className="text-xs font-medium text-foreground-600 mb-1.5">Qualité</p>
          <div className="flex flex-wrap gap-1.5 mb-4">{Z_QUALITIES.map((q) => (
            <button key={q.value} type="button" onClick={() => setQuality(q.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${quality === q.value ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600'}`}>{q.label}</button>
          ))}</div>

          {error && <div className="mb-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg"><i className="ri-error-warning-line"></i>{error}</div>}
          {running && <div className="mb-3 flex items-center gap-2 text-xs text-foreground-600 bg-background-100 px-3 py-2 rounded-lg"><i className="ri-loader-4-line animate-spin text-primary-500"></i>{progress}</div>}
          {done && <div className="mb-3 flex items-center gap-2 text-sm text-accent-700 bg-accent-100 px-3 py-2 rounded-lg"><i className="ri-checkbox-circle-line"></i>Lookbook généré.</div>}

          <button type="button" onClick={run} disabled={running || selected.length === 0} className="w-full flex items-center justify-center gap-1.5 px-5 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
            {running ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-book-open-line"></i>}
            {running ? 'Génération…' : 'Générer le lookbook'}
          </button>
          {done && (
            <Link to="/dashboard/zstudio/creations" className="mt-2 w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-archive-2-line"></i>Voir le lookbook
            </Link>
          )}
          <p className="text-[11px] text-foreground-400 mt-3">Chaque page contient : modèle, vêtement, nom, description, référence et prix réel (jamais inventé).</p>
        </div>
      </div>
    </div>
  );
}